import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type IssueType, type Stored } from "@overmap-ai/common"
import { selectIssueTypesOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useIssueTypeTable, useIssueTypeTableColumns } from "../../components/Tables/IssueTypeTable"
import { Table } from "../../components/Tables/Table"
import { issueTypeFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/")

export const IssueTypeSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(organizationId))
	const navigate = useNavigate()

	const handleRowClick = useCallback(
		(issueType: Stored<IssueType>) => {
			void navigate({
				from: "/$organizationId/settings/issue-types/",
				to: "/$organizationId/settings/issue-types/$issueTypeId",
				params: (prev) => ({ ...prev, issueTypeId: issueType.uuid }),
			})
		},
		[navigate],
	)

	const [filteredIssueTypes, filterValue, setFilterValue] = useTextFilter(issueTypes, issueTypeFilterCallback)

	const columns = useIssueTypeTableColumns()

	const table = useIssueTypeTable({
		data: filteredIssueTypes,
		columns: columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Issue types</Heading>
					<Text size="sm" accentColor="base">
						Manage the issue types in your organization.
					</Text>
				</div>

				<Separator size="full" />
				<div className="flex min-h-0 w-full grow flex-col gap-2">
					<div className="flex w-full justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<Link
							from="/$organizationId/settings/issue-types/"
							to="/$organizationId/settings/issue-types/create"
						>
							<Button size="sm" accentColor="primary" variant="surface">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</Link>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							size="sm"
							className="w-full"
							variant="surface"
							table={{ ...table }}
							onRowClick={handleRowClick}
							interactive
							emptyText="No issue types."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsView.displayName = "IssueTypeSettingsView"
